import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Visibility } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { InvoicingDocumentResponse } from '@/api/bol';
import {
  formatDateTime,
  getEntityDisplayName,
  formatExternalReferences,
  formatValueWithUnit,
} from '@/utils/datagridFormatters';

interface InvoicingDocumentColumnsProps {
  onDelete: (invoicingDocument: InvoicingDocumentResponse) => void;
  canDelete?: boolean;
  deleteTooltip?: string;
}

export const createInvoicingDocumentColumns = ({ onDelete, canDelete = true, deleteTooltip = '' }: InvoicingDocumentColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1.6,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { id?: string }) => row.id || '-',
  },
  {
    field: 'documentType',
    headerName: 'Document Type',
    flex: 1,
    minWidth: 140,
    valueGetter: (_value: unknown, row: { documentType?: string }) => row.documentType || '-',
  },
  {
    field: 'invoiceDate',
    headerName: 'Invoice Date',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { invoiceDate?: string }) => formatDateTime(row.invoiceDate),
  },
  {
    field: 'dueDate',
    headerName: 'Due Date',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { dueDate?: string }) => formatDateTime(row.dueDate),
  },
  {
    field: 'invoicingParty',
    headerName: 'Invoicing Party',
    flex: 1.2,
    minWidth: 170,
    valueGetter: (_value: unknown, row: { invoicingParty?: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null } }) =>
      getEntityDisplayName(row.invoicingParty),
  },
  {
    field: 'invoicedParty',
    headerName: 'Invoiced Party',
    flex: 1.2,
    minWidth: 170,
    valueGetter: (_value: unknown, row: { invoicedParty?: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null } }) =>
      getEntityDisplayName(row.invoicedParty),
  },
  {
    field: 'totalAmount',
    headerName: 'Total Amount',
    flex: 0.9,
    minWidth: 130,
    valueGetter: (_value: unknown, row: { totalAmount?: { value?: number | null; unit?: string | null } }) =>
      formatValueWithUnit(row.totalAmount?.value, row.totalAmount?.unit),
  },
  {
    field: 'totalAmountIncludingTaxes',
    headerName: 'Total Incl. Taxes',
    flex: 1,
    minWidth: 160,
    valueGetter: (_value: unknown, row: { totalAmountIncludingTaxes?: { value?: number | null; unit?: string | null } }) =>
      formatValueWithUnit(row.totalAmountIncludingTaxes?.value, row.totalAmountIncludingTaxes?.unit),
  },
  {
    field: 'invoiceLinesCount',
    headerName: 'Invoice Lines',
    flex: 0.8,
    minWidth: 120,
    valueGetter: (_value: unknown, row: { invoiceLines?: unknown[] }) => row.invoiceLines?.length || 0,
  },
  {
    field: 'invoiceTaxesCount',
    headerName: 'Invoice Taxes',
    flex: 0.8,
    minWidth: 120,
    valueGetter: (_value: unknown, row: { invoiceTaxes?: unknown[] }) => row.invoiceTaxes?.length || 0,
  },
  {
    field: 'externalReferences',
    headerName: 'External References',
    flex: 1.5,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { externalReferences?: Array<{ externalReferenceType?: string | null; reference?: string | null }> }) =>
      formatExternalReferences(row.externalReferences),
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 120,
    getActions: (params) => [
      <Tooltip key="view" title="View details">
        <IconButton
          component={Link}
          to={`/invoicing-documents/${params.row.id as string}`}
          size="small"
          color="primary"
          aria-label="View Details"
        >
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>,
      <GridActionsCellItem
        key="delete"
        icon={
          <Tooltip title={!canDelete ? deleteTooltip : 'Delete'}>
            <span>
              <Delete color={!canDelete ? 'disabled' : 'error'} />
            </span>
          </Tooltip>
        }
        label="Delete"
        onClick={() => onDelete(params.row as InvoicingDocumentResponse)}
        disabled={!canDelete}
        showInMenu={false}
      />,
    ],
  },
];

export const defaultInvoicingDocumentColumnVisibilityModel = {
  id: true,
  documentType: true,
  invoiceDate: true,
  dueDate: true,
  invoicingParty: true,
  invoicedParty: true,
  totalAmount: true,
  totalAmountIncludingTaxes: true,
  invoiceLinesCount: false,
  invoiceTaxesCount: false,
  externalReferences: false,
};
